/**
 * Fetch wrapper with cookie-based session auth.
 * 401 from /api/auth/* → emit 'app:session-expired' so AuthContext can clear state.
 * 401 from elsewhere is left to the caller (Twitch-API errors are mapped server-side
 * to 403 to avoid false logouts).
 */

const DEFAULT_BASE = (() => {
  if (typeof window === 'undefined') return '';
  return ''; // same-origin
})();

export interface ApiError extends Error {
  status: number;
  body: unknown;
}

export const SESSION_EXPIRED_EVENT = 'app:session-expired';

export async function apiFetch<T = unknown>(
  path: string,
  init?: RequestInit,
): Promise<T> {
  const url = path.startsWith('http') ? path : `${DEFAULT_BASE}${path}`;
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  let body: unknown = null;
  const text = await res.text();
  if (text.length > 0) {
    try {
      body = JSON.parse(text);
    } catch {
      body = text;
    }
  }

  if (!res.ok) {
    // Only treat 401 from /api/auth/me as "session expired" — other 401s may come
    // from rate-limit or upstream errors that should NOT log the user out.
    if (res.status === 401 && path.startsWith('/api/auth/')) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }
    const err = new Error(`API ${res.status} on ${path}`) as ApiError;
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body as T;
}
